import { useEffect, useMemo, useState } from 'react';

import { MediaItem } from '../../../app/services/mediaItems';
import { isMaSequenceFileName, isRppFileName } from '../../../utils/maSequenceFile';
import { linkMaSequence } from './linkMaSequence';
import { MaSequence } from './maTypes';
import { parseMaCsv } from './parseMaCsv';
import { isMaSequenceCsvText, parseMaDisplayNames } from './parseMaDisplayNames';
import { parseRppProject } from './parseRppProject';

/**
 * ショットのメディア一覧から MA 関連ファイル（CSV / RPP）を検出し、
 * fetch → 分類 → パース → メディアアイテムへのリンクまでを行うフック。
 *
 * CSV は内容で 2 種類に自動判別する（isMaSequenceCsvText）:
 * - シーケンス CSV（ヘッダーに media / start）: RPP と同じシーケンス候補
 * - 表示名 CSV（それ以外）: テロップ・タイムチャートの表示名マップとして
 *   すべてマージする（同じキーは updated_at が新しいファイルを優先）
 *
 * - シーケンス候補（RPP + シーケンス CSV）が複数ある場合は
 *   updated_at が最新の 1 つを有効とする
 * - ファイルテキストはモジュールキャッシュ（id + updated_at キー）に保持
 * - 文字コードは UTF-8 前提。文字化け（U+FFFD）検出時は Shift_JIS で再デコード
 * - 取得はファイルごとに並行し、解決した分から逐次反映する
 *   （表示名 CSV の取得がシーケンス表示をブロックしない）
 */

/** メディアアイテムが MA 関連ファイル（CSV / RPP）かどうか */
function isMaSequenceFile(item: MediaItem): boolean {
  return isMaSequenceFileName(item.file_name || '') || isMaSequenceFileName(item.file_key || '');
}

/** Reaper プロジェクトファイル（.rpp）かどうか（パーサ分岐用） */
function isRppFile(item: MediaItem): boolean {
  return isRppFileName(item.file_name || '') || isRppFileName(item.file_key || '');
}

const maFileTextCache = new Map<string, string>();

function cacheKeyOf(item: MediaItem): string {
  return `${item.id}:${item.updated_at}`;
}

async function fetchMaFileText(url: string, signal: AbortSignal): Promise<string> {
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`MA sequence file fetch failed: ${res.status}`);
  const buffer = await res.arrayBuffer();
  const utf8 = new TextDecoder('utf-8').decode(buffer);
  // 文字化け検出時のみ Shift_JIS（Excel 保存 CSV）で再トライ
  if (utf8.includes('\uFFFD')) {
    try {
      return new TextDecoder('shift_jis').decode(buffer);
    } catch {
      return utf8;
    }
  }
  return utf8;
}

export interface MaSequenceState {
  /** アクティブな MA シーケンスファイル（シーケンス分類のうち updated_at 最新）。無ければ null */
  maFileItem: MediaItem | null;
  /** ショット内の全 MA 関連ファイル（CSV / RPP。表示名 CSV 含む）の ID（サイドバー非表示用） */
  maFileItemIds: Set<string>;
  /** リンク済みシーケンス（ファイルなし / 読込中 / 失敗時は null） */
  sequence: MaSequence | null;
  /** 表示名 CSV のマージ結果（sequence.displayNames と同じ。無ければ空 Map） */
  displayNames: Map<string, string>;
  isLoading: boolean;
  /**
   * MA ファイルが存在し、まだ解決（fetch → パース）が終わっていない間 true。
   * isLoading と違い、fetch を開始する effect の実行前（初回レンダー）から
   * true になるため、「個別アイテム表示 → MA 表示」の切替ちらつきを
   * 防ぐ表示ゲートに使える。解決後（シーケンスなし確定・取得失敗含む）は false
   */
  isResolving: boolean;
  /** ファイルの取得・デコード失敗（パース警告は sequence.warnings 側） */
  fetchError: string | null;
}

const EMPTY_ID_SET = new Set<string>();
const EMPTY_DISPLAY_NAMES = new Map<string, string>();

/** アイテム ID → ファイルテキスト（取得失敗は null = 解決済み扱い） */
interface MaFileTextsState {
  key: string | null;
  texts: Map<string, string | null> | null;
  /** 未解決（fetch 中）のファイル数 */
  pending: number;
  error: string | null;
}

/**
 * RPP で「ひとつ前の動画から共通部分の音を引き継ぐ」かの既定値
 * （MaLinkOptions.inheritFromPreviousSegment。CSV には常に適用しない）
 */
export const DEFAULT_MA_INHERIT_FROM_PREVIOUS_SEGMENT = true;

export interface UseMaSequenceOptions {
  /** 未指定は DEFAULT_MA_INHERIT_FROM_PREVIOUS_SEGMENT */
  inheritFromPreviousSegment?: boolean;
}

export function useMaSequence(
  mediaItems: MediaItem[] | undefined,
  options: UseMaSequenceOptions = {},
): MaSequenceState {
  const { inheritFromPreviousSegment = DEFAULT_MA_INHERIT_FROM_PREVIOUS_SEGMENT } = options;
  const items = useMemo(() => mediaItems ?? [], [mediaItems]);

  const maFileItems = useMemo(() => items.filter(isMaSequenceFile), [items]);

  const maFileItemIds = useMemo(() => {
    const ids = new Set(maFileItems.map(item => item.id));
    return ids.size > 0 ? ids : EMPTY_ID_SET;
  }, [maFileItems]);

  // MA 関連ファイル一式の同一性キー（アイテム・テイクの増減や更新で変わる）
  const batchKey =
    maFileItems.length > 0
      ? maFileItems
          .map(cacheKeyOf)
          .sort()
          .join('|')
      : null;

  const [textState, setTextState] = useState<MaFileTextsState>({
    key: null,
    texts: null,
    pending: 0,
    error: null,
  });

  useEffect(() => {
    if (!batchKey) {
      // 同値なら state を据え置く（親から不安定な配列が渡っても再レンダーをループさせない）
      setTextState(prev =>
        prev.key === null ? prev : { key: null, texts: null, pending: 0, error: null },
      );
      return;
    }

    // キャッシュ命中分は同期で反映し、未命中のみ並行 fetch
    const initial = new Map<string, string | null>();
    const missing: MediaItem[] = [];
    maFileItems.forEach(item => {
      const cached = maFileTextCache.get(cacheKeyOf(item));
      if (cached !== undefined) initial.set(item.id, cached);
      else missing.push(item);
    });
    if (missing.length === 0) {
      // 全キャッシュ命中: 既に同じキーで解決済みなら据え置く（上と同じループ対策）
      setTextState(prev =>
        prev.key === batchKey && prev.pending === 0 && !prev.error
          ? prev
          : { key: batchKey, texts: initial, pending: 0, error: null },
      );
      return;
    }
    setTextState({ key: batchKey, texts: initial, pending: missing.length, error: null });

    const controller = new AbortController();
    let cancelled = false;

    missing.forEach(item => {
      (async () => {
        let text: string | null = null;
        try {
          text = await fetchMaFileText(item.original_url, controller.signal);
          maFileTextCache.set(cacheKeyOf(item), text);
        } catch (e) {
          if (cancelled || controller.signal.aborted) return;
          console.warn('[useMaSequence] MA ファイルの取得に失敗:', item.file_name, e);
        }
        if (cancelled) return;
        setTextState(prev => {
          if (prev.key !== batchKey || !prev.texts) return prev;
          const texts = new Map(prev.texts);
          texts.set(item.id, text);
          return {
            key: prev.key,
            texts,
            pending: prev.pending - 1,
            error: text === null ? 'MA シーケンスファイルの読み込みに失敗しました' : prev.error,
          };
        });
      })();
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [batchKey, maFileItems]);

  // 取得済みテキストの分類: アクティブなシーケンスファイルの確定 + 表示名のマージ
  const resolved = useMemo(() => {
    if (textState.key !== batchKey || !textState.texts) return null;
    const texts = textState.texts;

    let candidate: { item: MediaItem; text: string } | null = null;
    const nameFiles: { item: MediaItem; text: string }[] = [];
    for (const item of maFileItems) {
      const text = texts.get(item.id);
      if (text == null) continue; // 未解決 or 取得失敗
      if (isRppFile(item) || isMaSequenceCsvText(text)) {
        if (
          !candidate ||
          new Date(item.updated_at).getTime() > new Date(candidate.item.updated_at).getTime()
        ) {
          candidate = { item, text };
        }
      } else {
        nameFiles.push({ item, text });
      }
    }

    // 採用確定の条件: 候補より新しい未解決ファイルが残っていないこと
    // （後から解決したファイルがシーケンス採用を奪って表示が切り替わるのを防ぐ）
    if (candidate) {
      const candidateTime = new Date(candidate.item.updated_at).getTime();
      const hasNewerUnresolved = maFileItems.some(
        item => !texts.has(item.id) && new Date(item.updated_at).getTime() > candidateTime,
      );
      if (hasNewerUnresolved) candidate = null;
    }

    // 表示名 CSV を古い順にマージ（同じキーは新しいファイルが勝つ）
    const displayNames = new Map<string, string>();
    [...nameFiles]
      .sort((a, b) => new Date(a.item.updated_at).getTime() - new Date(b.item.updated_at).getTime())
      .forEach(({ text }) => {
        parseMaDisplayNames(text).forEach((value, key) => displayNames.set(key, value));
      });

    return { candidate, displayNames };
  }, [textState, batchKey, maFileItems]);

  const sequence = useMemo((): MaSequence | null => {
    const candidate = resolved?.candidate;
    if (!candidate) return null;
    // 拡張子でパーサを分岐（RPP は内部で CSV 相当の行データに変換される）
    const isRpp = isRppFile(candidate.item);
    const parsed = isRpp ? parseRppProject(candidate.text) : parseMaCsv(candidate.text);
    // MA 関連ファイル自体はリンク対象から除外してリンクする
    const linkTargets = items.filter(item => !maFileItemIds.has(item.id));
    // 保存 CSV には引き継いだ音が明示行として書かれるため、CSV では引き継がない
    const linked = linkMaSequence(parsed.rows, linkTargets, parsed.optionSlots, {
      inheritFromPreviousSegment: isRpp && inheritFromPreviousSegment,
      rulerLanes: parsed.rulerLanes,
      videoTrackName: parsed.videoTrackName,
    });
    return {
      ...linked,
      warnings: [...parsed.warnings, ...linked.warnings],
      displayNames: resolved.displayNames,
    };
  }, [resolved, items, maFileItemIds, inheritFromPreviousSegment]);

  return {
    maFileItem: resolved?.candidate?.item ?? null,
    maFileItemIds,
    sequence,
    displayNames: resolved?.displayNames ?? EMPTY_DISPLAY_NAMES,
    isLoading: textState.key === batchKey && textState.pending > 0,
    // MA ファイルがあり、テキスト解決が終わるまで true（effect 実行前の
    // 初回レンダー = textState.key 不一致も含む）。取得失敗時は解決済み扱い
    //（texts に null が入り pending が減る）ので、エラー時は従来表示に戻る
    isResolving:
      batchKey !== null && (textState.key !== batchKey || textState.pending > 0),
    fetchError: textState.error,
  };
}
